import { FlatStyleLike } from 'ol/style/flat';
import { WebGLLayer } from '../map/webglLayer';
import { FeatureLike } from '../utils/geoJsonUtils';
import GeoJSON from 'ol/format/GeoJSON';
import VectorSource from 'ol/source/Vector';
import { useEffect, useMemo } from 'react';
import VectorLayer from 'ol/layer/Vector';
import { StyleLike } from 'ol/style/Style';
import { useMapContext } from '../map/context/MapContext';

export enum OlLayerType {
  WebGL = 'WebGL',
  Vector = 'vector',
}

type OlStyle = StyleLike | FlatStyleLike;

type useMapLayerOptions = {
  zIndex: number;
  style?: OlStyle;
  layerType?: OlLayerType;
  opacity?: number;
  properties?: Record<string, any>;
};

export const useMapLayer = (
  features: FeatureLike[],
  {
    zIndex,
    style = {},
    layerType = OlLayerType.WebGL,
    opacity = 1,
    properties,
  }: useMapLayerOptions,
) => {
  const { map } = useMapContext();
  const source = useMemo(() => {
    return new VectorSource({
      format: new GeoJSON(),
    });
  }, []);

  const layer = useMemo(() => {
    switch (layerType) {
      case OlLayerType.Vector:
        return new VectorLayer({
          source,
          style,
          zIndex,
          properties,
        });
      case OlLayerType.WebGL:
      default:
        return new WebGLLayer({
          source,
          style,
          zIndex,
          opacity,
          properties,
        });
    }
  }, [source, style, opacity, layerType, properties]);

  const geoJsonFeatures = useMemo(() => {
    return new GeoJSON().readFeatures({
      type: 'FeatureCollection',
      features,
    });
  }, [features]);

  useEffect(() => {
    // if (!layer) return;
    source.addFeatures(geoJsonFeatures);
    return () => {
      source.clear();
    };
  }, [geoJsonFeatures, source]);

  useEffect(() => {
    if (!layer) return;
    layer.setZIndex(zIndex);
  }, [zIndex, layer]);

  useEffect(() => {
    if (!layer) return;
    return () => {
      layer.dispose();
    };
  }, [layer]);

  useEffect(() => {
    if (!layer) return;
    layer.setOpacity(opacity);
  }, [opacity, layer]);

  useEffect(() => {
    if (!map || !layer) return;
    map.addLayer(layer);
    return () => {
      if (map.getAllLayers().includes(layer)) {
        map.removeLayer(layer);
      }
    };
  }, [map, layer]);

  return layer;
};
